import { TournamentType } from "@ssf/memdb-api";
import { z } from "zod";
import { defaultTimeControls } from "./constants.js";
import { parseTimeControlString } from "./timeControlString.js";
export { z };
const moveLimitSchema = z
    .number()
    .int()
    .nonnegative()
    .safe()
    .or(z
    .string()
    .trim()
    .regex(/^\d+$/)
    .transform(Number)
    .pipe(z.number().int().nonnegative().safe()))
    .optional();
const roomBase = z.object({
    name: z.string().optional(),
    description: z.string().optional(),
    autoStart: z
        .boolean()
        .or(z.enum(["on", "off"]).transform((value) => value === "on"))
        .optional(),
    hideResignButton: z
        .boolean()
        .or(z.enum(["on", "off"]).transform((value) => value === "on"))
        .optional(),
    minimumMovesForResign: moveLimitSchema,
    minimumMovesForDrawOffer: moveLimitSchema,
    disallowDrawOfferBeforeMove10: z
        .boolean()
        .or(z.enum(["on", "off"]).transform((value) => value === "on"))
        .optional(),
    disallowDrawOfferBeforeMove30: z
        .boolean()
        .or(z.enum(["on", "off"]).transform((value) => value === "on"))
        .optional(),
    antiCheatCheck: z
        .boolean()
        .or(z.enum(["on", "off"]).transform((value) => value === "on"))
        .optional(),
    hideResults: z
        .boolean()
        .or(z.enum(["on", "off"]).transform((value) => value === "on"))
        .optional(),
    privacyStatus: z
        .enum(["public", "publicAlways", "unlisted"])
        .catch("unlisted"),
    startPosition: z.string().optional(),
    timeControl: z
        .enum(defaultTimeControls)
        .optional()
        .default(defaultTimeControls[0]),
});
export const challengeRoomSchema = roomBase.merge(z.object({
    type: z.literal("challenge"),
}));
export const tournamentRoomSchema = roomBase.merge(z.object({
    type: z.literal("tournament"),
    memdbGroupId: z.number().or(z.string().regex(/\d+/).transform(Number)),
    message: z.string().optional(),
    updated: z
        .date()
        .or(z.string().transform((date) => new Date(date)))
        .or(z.number().transform((timestamp) => new Date(timestamp))),
}));
export const roomSchema = z.union([challengeRoomSchema, tournamentRoomSchema]);
export const changeObjectSchema = z
    .object({
    changeType: z.literal("GROUPSETTINGSCHANGE"),
    tournamentType: z.nativeEnum(TournamentType).optional(),
    groupId: z.number(),
})
    .or(z.object({
    changeType: z.literal("PAIRINGCHANGE"),
    tournamentType: z.nativeEnum(TournamentType).optional(),
    groupId: z.number(),
    pairingId: z.number().optional(),
    pairingIds: z.array(z.number()).optional(),
}))
    .or(z.object({
    changeType: z.literal("ARENA_PAIRING_PING"),
    tournamentType: z.nativeEnum(TournamentType).optional(),
    groupId: z.number(),
}))
    .or(z.object({
    changeType: z.literal("GAMECHANGE"),
    tournamentType: z.nativeEnum(TournamentType).optional(),
    groupId: z.number(),
    pairingId: z.number(),
    board: z.number(),
}))
    .or(z.object({
    changeType: z.literal("GAMECHANGE"),
    tournamentType: z.literal(TournamentType.Yes2Chess),
    pairingId: z.number(),
    board: z.number(),
}))
    .or(z.object({
    changeType: z.literal("GROUPSETTINGSCHANGE"),
    groupId: z.number(),
}));
export const firebaseArray = (schema) => z
    .array(schema)
    .or(z.record(schema).transform((value) => Object.values(value)));
const playerSchema = z.object({
    uid: z.string(),
    name: z.string(),
});
export const guestIdSchema = z.string().uuid();
export const guestUid = (guestId) => `guest_${guestIdSchema.parse(guestId)}`;
const timeControlSchema = z.object({
    limit: z.number(),
    increment: z.number(),
});
export const arenaPairingSchema = z.object({
    pairingId: z.number(),
    board: z.number(),
    createdAt: z.number(),
    white: playerSchema,
    black: playerSchema,
    timeControl: timeControlSchema,
    autoStart: z.boolean().optional(),
    antiCheatCheck: z.boolean().optional(),
});
export const arenaPlayerPairingSchema = z.object({
    pairingId: z.number(),
    board: z.number(),
    createdAt: z.number(),
    color: z.enum(["white", "black"]),
    opponentUid: z.string(),
});
export const suspicionStatsSchema = z.object({
    isSuspicious: z.boolean(),
    movesWithEvents: z.number(),
    totalMoves: z.number(),
    consecutiveMovesWithEvents: z.number(),
    percentageOfMovesWithEvents: z.number(),
    color: z.enum(["white", "black"]).optional(),
    playerName: z.string().optional(),
});
export const gameSchema = z.object({
    white: playerSchema,
    black: playerSchema,
    createdBy: z.string(),
    createdAt: z.number(),
    updatedAt: z.number().optional(),
    status: z.number(),
    result: z.number(),
    resultAt: z.number().optional(),
    timeControl: z.object({
        white: firebaseArray(timeControlSchema),
        black: firebaseArray(timeControlSchema),
    }),
    meta: z
        .object({
        memdb: z.boolean().optional(),
        arena: z.boolean().optional(),
        pairingId: z.number().optional(),
        board: z.number().optional(),
    })
        .optional(),
    autoStart: z.boolean().optional(),
    antiCheatCheck: z.boolean().optional(),
});
export const nodeSchema = z.object({
    blackTime: z.number(),
    whiteTime: z.number(),
    position: z.string().optional(),
    move: z.number().optional(),
    start: z.boolean().optional(),
    timestamp: z.number(),
    next: firebaseArray(z.number()).optional(),
    draw: z.number().optional(),
    undo: z.enum(["inactive", "admin"]).optional(),
});
export const nodesSchema = firebaseArray(nodeSchema);
export const challengeSchema = z.object({
    user: playerSchema,
    color: z.number().or(z.string().transform((str) => Number.parseInt(str))),
    timeControl: timeControlSchema.or(z
        .string()
        .transform((timeControlString) => parseTimeControlString(timeControlString))),
    accepted: z
        .object({ uid: z.string(), displayName: z.string().optional() })
        .optional(),
    createdAt: z.number(),
});
export const databases = [
    "default-rtdb",
    "games",
    "games-history",
    "nodes",
    "nodes-history",
    "yes2chess",
];
export const getDatabaseFromString = (database) => {
    const db = database.replace(/\/.*/, "");
    return databases.includes(db) ? db : databases[0];
};
export const getDatabaseFromPath = (path) => {
    const [root, room] = path.join("/").split("/");
    if (root === "pendingArenaResults") {
        return "games";
    }
    return room === "yes2chess" && (root === "games" || root === "nodes")
        ? "yes2chess"
        : getDatabaseFromString(root);
};
//# sourceMappingURL=schema.js.map